import { useRef, useState, type CSSProperties, type TouchEvent } from 'react';

/** Phones and tablets only: a mouse can't swipe. */
const touchScreen = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

/** How far a message has to be pulled before letting go replies to it. */
export const SWIPE_REPLY_PX = 56;
const MAX_PULL_PX = 80;

/**
 * Swipe a message left to reply to it (phones). Mostly-vertical drags are left
 * to the scroll, so it never fights with scrolling the chat.
 */
export function useSwipeToReply(onReply: () => void, enabled: boolean) {
  const [pull, setPull] = useState(0);
  const start = useRef<{ x: number; y: number; axis: 'x' | 'y' | null } | null>(null);
  const pulled = useRef(0);

  if (!enabled || !touchScreen) return { pull: 0, handlers: {}, style: undefined as CSSProperties | undefined };

  const reset = () => {
    start.current = null;
    pulled.current = 0;
    setPull(0);
  };

  const handlers = {
    onTouchStart: (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length > 1) return;
      start.current = { x: t.clientX, y: t.clientY, axis: null };
    },
    onTouchMove: (e: TouchEvent) => {
      const s = start.current;
      const t = e.touches[0];
      if (!s || !t) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (!s.axis) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        s.axis = Math.abs(dx) > Math.abs(dy) * 1.5 ? 'x' : 'y';
      }
      if (s.axis !== 'x') return;
      const next = Math.max(0, Math.min(MAX_PULL_PX, -dx));
      if (pulled.current < SWIPE_REPLY_PX && next >= SWIPE_REPLY_PX) navigator.vibrate?.(8);
      pulled.current = next;
      setPull(next);
    },
    onTouchEnd: () => {
      if (pulled.current >= SWIPE_REPLY_PX) onReply();
      reset();
    },
    onTouchCancel: reset,
  };

  const style: CSSProperties = { transform: pull ? `translateX(${-pull}px)` : undefined, transition: pull ? 'none' : 'transform 160ms ease-out' };
  return { pull, handlers, style };
}
